import {
  helpWidgetDragWidgetNewSrc4Evid1508,
  helpWidgetLoadWidgetInBackgroundNewSrc4Evid1501,
  helpWidgetWidgetCloseNewSrc4Evid1503,
} from '@wix/bi-logger-customer-care-data/v2';
import webBiLogger from '@wix/web-bi-logger';
import { BiDataWidgetPlacement, WidgetClosingTrigger } from '@wix/wix-chatbot-common/bi';
import { getBiAreaAndPlatform } from '@wix/wix-chatbot-common/browser';
import { SupportChatOrigins } from '@wix/wix-help-widget-common/src/types';

import { getState } from '../store';
import { getContext } from '../utils/context';
import { getCurrentPage, pageEnumToString } from '../utils/page';
import { widgetModel } from '../widgetModel';

const logger = webBiLogger.factory().logger();

// Common BI context interface
interface BiContext {
  context_area: string;
  context_platform: string;
  context_url: string;
  target_account_id?: string;
}

// Helper to get common BI context
const getBiContext = (): BiContext => {
  const url = new URL(window.location.href);
  const { contextArea, contextPlatform } = getBiAreaAndPlatform(url.href);
  const {
    context: { user },
  } = getState();

  return {
    context_area: contextArea,
    context_platform: contextPlatform,
    context_url: `${url.origin}${url.pathname}`, // URL without query params
    target_account_id: user?.accountId,
  };
};

// Helper to get widget language with fallback
const getWidgetLanguage = (): string => {
  const {
    chatbot: { conversation },
  } = getState();

  // Try conversation language first, then fallback to widget model locale
  return conversation.lang || widgetModel.getLocale() || '';
};

// Helper to get open source info for specific scenarios
const getOpenSourceInfo = (
  origin: SupportChatOrigins,
  placement: BiDataWidgetPlacement | undefined,
): string | undefined => {
  switch (placement) {
    case BiDataWidgetPlacement.GFPP:
      // For GFPP scenarios - placement is set to 'gfpp' when opened from GFPP buttons
      switch (origin) {
        case 'Editor3':
          return 'editor3_gfpp';
        case 'StudioEditor':
          return 'studio_editor_gfpp';
        case 'Editor':
          return 'editor_gfpp';
        default:
          // Fallback for other origins with GFPP placement
          return `${origin.toLowerCase()}_gfpp`;
      }
    case BiDataWidgetPlacement.Header:
      // For header placements, provide origin-specific info
      return `${origin.toLowerCase()}_header`;
    default:
      // For other placements, return undefined (no additional info needed)
      return undefined;
  }
};

// Event specific logging functions
export const logHelpWidgetLoadInBackground = async () => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation },
  } = getState();

  const { metaSiteId } = context;

  logger.report(
    helpWidgetLoadWidgetInBackgroundNewSrc4Evid1501({
      context_msid: metaSiteId,
      notification_count: conversation.unreadCount || 0,
      ...biContext,
    }),
  );
};

// New BI event for widget open/close - evid 1503
export const logHelpWidgetWidgetOpenClose = async (closingTrigger?: WidgetClosingTrigger) => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation, chatbotActiveSession },
    widgetSessionId,
    bi: { placement },
    initParams: { origin },
  } = getState();

  const { metaSiteId } = context;
  const isClosing = !!closingTrigger;

  let conversationId = conversation.conversationId;
  if (!conversationId && chatbotActiveSession?.conversationId) {
    conversationId = chatbotActiveSession.conversationId;
  }

  logger.report(
    helpWidgetWidgetCloseNewSrc4Evid1503({
      chatbot_conversation_id: conversationId || undefined,
      closing_trigger: closingTrigger,
      context_msid: metaSiteId,
      // For closing, both open_source and open_source_info should be undefined
      open_source: isClosing ? undefined : placement || undefined,
      open_source_info: isClosing ? undefined : getOpenSourceInfo(origin, placement),
      self_service_session_id: widgetSessionId,
      widget_language: getWidgetLanguage(),
      widget_page_type: pageEnumToString(getCurrentPage()),
      ...biContext,
    }),
  );
};

export const logHelpWidgetDragWidget = async () => {
  const biContext = getBiContext();
  const context = await getContext();
  const {
    chatbot: { conversation },
    widgetSessionId,
  } = getState();

  logger.report(
    helpWidgetDragWidgetNewSrc4Evid1508({
      ...biContext,
      context_msid: context.metaSiteId,
      self_service_session_id: widgetSessionId,
      chatbot_conversation_id: conversation.conversationId,
      widget_language: getWidgetLanguage(),
      widget_page_type: pageEnumToString(getCurrentPage()),
    }),
  );
};
